import axios from 'axios';

const ACTIVE_PLAYLIST_KEY = 'tt.music.activePlaylist';

function envelopeItem(data) {
  return data?.response?.[0];
}

export function fetchAppleMusicDeveloperToken() {
  return axios
    .get('/apple-music-developer-token/')
    .then(({ data }) => envelopeItem(data));
}

export function fetchAppleMusicConnection() {
  return axios
    .get('/apple-music-connection/')
    .then(({ data }) => envelopeItem(data));
}

export function connectAppleMusic({ music_user_token, display_name } = {}) {
  const body = { music_user_token };
  if (display_name) body.display_name = display_name;
  return axios
    .post('/apple-music-connect/', body)
    .then(({ data }) => envelopeItem(data));
}

export function disconnectAppleMusic() {
  return axios
    .delete('/apple-music-connection/')
    .then(({ data }) => envelopeItem(data));
}

export function saveAppleMusicTrack(trackId) {
  return axios
    .put('/apple-music-library/tracks/', { track_id: trackId })
    .then(({ data }) => envelopeItem(data));
}

export function removeAppleMusicTrack(trackId) {
  return axios
    .delete('/apple-music-library/tracks/', { data: { track_id: trackId } })
    .then(({ data }) => envelopeItem(data));
}

export function normalizeFeatured(featured) {
  if (Array.isArray(featured)) {
    return {
      data: featured,
      total: featured.length,
      limit: featured.length,
      offset: 0,
      next: false,
    };
  }
  if (featured && typeof featured === 'object') {
    const data = Array.isArray(featured.data) ? featured.data : [];
    return {
      data,
      total: featured.total ?? data.length,
      limit: featured.limit ?? data.length,
      offset: featured.offset ?? 0,
      next: Boolean(featured.next),
    };
  }
  return { data: [], total: 0, limit: 0, offset: 0, next: false };
}

export function isAppleMusicCurator(playlist) {
  return String(playlist?.owner_name || playlist?.ownerName || '')
    .toLowerCase()
    .includes('apple');
}

export function fetchAppleMusicHome() {
  return axios
    .get('/apple-music-music-home/')
    .then(({ data }) => {
      const payload = envelopeItem(data) || {};
      return {
        ...payload,
        featured: normalizeFeatured(payload.featured),
      };
    });
}

export function searchAppleMusic({ q, type, limit = 10, offset = 0 } = {}) {
  return axios
    .get('/apple-music-search/', {
      params: {
        q,
        type,
        limit,
        offset,
      },
    })
    .then(({ data }) => envelopeItem(data));
}

export function fetchFeaturedWorkoutPlaylists({ limit = 25, offset = 0 } = {}) {
  return searchAppleMusic({
    q: 'workout',
    type: 'playlist',
    limit,
    offset,
  }).then((payload) => {
    const playlists = (payload?.playlists || []).filter(isAppleMusicCurator);
    return {
      data: playlists,
      total: playlists.length,
      limit,
      offset,
      next: playlists.length >= limit,
    };
  });
}

export function fetchAppleMusicPlaylists({ limit = 20, offset = 0 } = {}) {
  return axios
    .get('/apple-music-playlists/', {
      params: { limit, offset },
    })
    .then(({ data }) => {
      const payload = envelopeItem(data);
      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        total: payload?.total ?? 0,
        next: Boolean(payload?.next),
      };
    });
}

export function fetchAppleMusicPlaylist(playlistId, { limit = 50, offset = 0 } = {}) {
  return axios
    .get(`/apple-music-playlists/${playlistId}/`, {
      params: { limit, offset },
    })
    .then(({ data }) => envelopeItem(data));
}

export function getActiveWorkoutPlaylist() {
  try {
    const raw = localStorage.getItem(ACTIVE_PLAYLIST_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.playlistId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setActiveWorkoutPlaylist(playlist) {
  if (!playlist?.playlistId) {
    localStorage.removeItem(ACTIVE_PLAYLIST_KEY);
    return null;
  }
  const payload = {
    playlistId: playlist.playlistId,
    name: playlist.name || 'Playlist',
    imageUrl: playlist.imageUrl || null,
    externalUrl: playlist.externalUrl || null,
    uri:
      playlist.uri ||
      (playlist.playlistId ? `applemusic:playlist:${playlist.playlistId}` : null),
  };
  localStorage.setItem(ACTIVE_PLAYLIST_KEY, JSON.stringify(payload));
  return payload;
}

export function clearActiveWorkoutPlaylist() {
  localStorage.removeItem(ACTIVE_PLAYLIST_KEY);
}

export { ACTIVE_PLAYLIST_KEY };
